import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import _ from "lodash";
import { useI18n } from "Hooks";
import classes from "./ParticipantsPreview.scss";
import withLoader from "UIComponents/GqlWithLoader";
import classNames from "classnames";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import { getFormattedUserName } from "Utils/GetFormattedUserName";

import { getUserCountByEntityandEntityIds } from "MyCalendar/components/EventModalV2/modules/EventModalV2Query";
import { getOrganizationId } from "modules/CommonSelectors";

// Utils
import {
  mapStateToProps,
  mapActionCreators,
  MAP_LIST_TYPE_TO_QUERY_ENTITY,
} from "../../modules/Utils";
import { PARTICIPANT_USER_TYPES } from "MyCalendar/modules/MyCalendarModule/MyCalendarConstants";

import { Avatar, Tabs } from "@toddle-edu/ds-web";
import { getSelectedAcademicYearIdForSchool } from "../../../../modules/Utils";

export const ENTITY_TYPE = {
  GRADE: "GRADE",
  GRADES: "GRADES",
  CLASS: "CLASS",
  CLASSES: "CLASSES",
  CUSTOM: "CUSTOM",
  STAFF_MEMBERS: "STAFF_MEMBERS",
  STUDENTS: "STUDENTS",
  PARENTS: "PARENTS",
  STAFF: "STAFF",
  STUDENT: "STUDENT",
  FAMILY_MEMBER: "FAMILY_MEMBER",
  PARENT: "PARENT",
  COURSES: "COURSES",
  COURSE: "COURSE",
};

const USER_TYPE_COUNT_FIELDS = {
  STAFF: "staffCount",
  STUDENT: "studentCount",
  FAMILY_MEMBER: "familyMemberCount",
  PARENT: "familyMemberCount",
};

const ListItem = ({ id, label, subText, isQuickViewModal }) => {
  const itemContainerClasses = classNames(classes.itemContainer, {
    [classes.itemContainerNoBorder]: isQuickViewModal,
  });
  return (
    <div className={itemContainerClasses}>
      <Avatar
        dsVersion="2.0"
        id={id}
        name={label}
        size="small"
        shape="square"
      />
      <div className={classes.itemDetail}>
        <div className={classNames("text-body", classes.itemLabel)}>
          {label}
        </div>
        <div className={classNames("text-body-s", classes.itemCountRow)}>
          {subText}
        </div>
      </div>
    </div>
  );
};

const getSubtextString = ({ t, item, sharedWith }) => {
  return _.reduce(
    sharedWith,
    (result, userType, index) => {
      const userTypeCount = _.get(item, USER_TYPE_COUNT_FIELDS[userType]);
      return _.concat(
        result,
        `${index ? ", " : ""}${userTypeCount} ${t(
          PARTICIPANT_USER_TYPES[userType],
          {
            count: userTypeCount,
          }
        )}`
      );
    },
    ""
  );
};

const UserParticipantList = ({ userByUserType, isQuickViewModal }) => {
  const { t } = useI18n(["common"]);

  const segmentOptions = useMemo(
    () => [
      { value: "STAFF", label: t("common:staff_member_other") },
      { value: "STUDENT", label: t("common:student_other") },
      { value: "PARENT", label: t("common:family_member_other") },
    ],
    []
  );

  const filteredSegmentOptions = useMemo(
    () =>
      _.filter(segmentOptions, option => _.size(userByUserType[option?.value])),
    [segmentOptions, userByUserType]
  );

  const [selectedUserType, setSelectedUserType] = useState(
    _.first(filteredSegmentOptions)?.value
  );

  const effectiveSelectedUserType = useMemo(() => {
    const isValid = _.some(
      filteredSegmentOptions,
      option => option.value === selectedUserType
    );
    return isValid
      ? selectedUserType
      : _.first(filteredSegmentOptions)?.value ?? null;
  }, [filteredSegmentOptions, selectedUserType]);

  const handleSegmentChange = value => {
    setSelectedUserType(value);
  };

  const tabsContainerClasses = classNames(classes.tabsContainer, {
    [classes.tabsContainerNoMargin]: isQuickViewModal,
  });

  const itemListClasses = classNames(classes.itemList, {
    [classes.itemListNoHorizontalPadding]: isQuickViewModal,
  });

  return (
    <div className={classes.userParticipantList}>
      <div className={tabsContainerClasses}>
        <Tabs
          dsVersion={"2.0"}
          options={filteredSegmentOptions}
          value={effectiveSelectedUserType}
          onChange={handleSegmentChange}
          type={"flexible"}
          variant={"inline"}
          containerStyle={{
            marginBottom: "8px",
          }}
        />
      </div>

      <div className={itemListClasses}>
        {_.map(userByUserType[effectiveSelectedUserType], user => {
          const userType = _.lowerCase(effectiveSelectedUserType);
          const formattedName = getFormattedUserName({ user, type: userType });
          return (
            <ListItem
              key={user.id}
              id={user.id}
              label={formattedName}
              isQuickViewModal={isQuickViewModal}
            />
          );
        })}
      </div>
    </div>
  );
};

const RightPreviewPanel = ({
  t,
  sharedWith,
  participantListType,
  grades,
  courses,
  userByUserType,
  isQuickViewModal,
  isAssociatedEntitiesDetailsLoading = true,
}) => {
  const itemListClasses = classNames(classes.itemList, {
    [classes.itemListNoPadding]: isQuickViewModal,
  });
  switch (participantListType) {
    case ENTITY_TYPE.CUSTOM:
      return (
        <UserParticipantList
          userByUserType={userByUserType}
          isQuickViewModal={isQuickViewModal}
        />
      );
    case ENTITY_TYPE.COURSE:
      return (
        <div className={itemListClasses}>
          {_.map(courses, item => {
            const { id, title } = item;
            return (
              <ListItem
                key={id}
                id={id}
                label={title}
                subText={
                  isAssociatedEntitiesDetailsLoading ? (
                    <SkeletonLoader height="16px" width="70%" />
                  ) : (
                    getSubtextString({ t, item, sharedWith })
                  )
                }
                isQuickViewModal={isQuickViewModal}
              />
            );
          })}
        </div>
      );
    case ENTITY_TYPE.GRADE:
      return (
        <div className={itemListClasses}>
          {_.map(grades, item => {
            const { id, name } = item;
            return (
              <ListItem
                key={id}
                id={id}
                label={name}
                subText={
                  isAssociatedEntitiesDetailsLoading ? (
                    <SkeletonLoader height="12px" width="56%" />
                  ) : (
                    getSubtextString({ t, item, sharedWith })
                  )
                }
                isQuickViewModal={isQuickViewModal}
              />
            );
          })}
        </div>
      );
    default:
      return <div></div>;
  }
};

const ParticipantsPreview = ({
  grades,
  courses,
  participantListType,
  sharedWith,
  userCountByEntity,
  userByUserType,
  updateEventModalV2,
  isQuickViewModal = false,
  isAssociatedEntitiesDetailsLoading,
}) => {
  const { t } = useI18n(["common"]);

  const staffCount = _.includes(sharedWith, "STAFF")
    ? userCountByEntity?.staffCount
    : 0;
  const studentCount = _.includes(sharedWith, "STUDENT")
    ? userCountByEntity?.studentCount
    : 0;
  const familyCount = _.includes(sharedWith, "FAMILY_MEMBER")
    ? userCountByEntity?.familyCount
    : 0;
  const totalParticipants = staffCount + studentCount + familyCount;

  useEffect(() => {
    if (!isQuickViewModal) {
      updateEventModalV2({
        totalParticipantUserCount: totalParticipants,
      });
    }
  }, [totalParticipants, isQuickViewModal]);

  return (
    <div className={classes.participantsPreview}>
      {/* Header Section */}
      {!isQuickViewModal ? (
        <div className={classes.header}>
          <div className={classNames("text-label-l", classes.headerTitle)}>
            {t("calendar:selected_participants_with_count", {
              count: totalParticipants,
            })}
          </div>
          <div className={classes.participantsSummary}>
            <div className={classes.summaryItem}>
              <span className="text-body">
                {t("common:staff_member", { count: staffCount })}
              </span>
              <span className={classNames("text-body-medium", classes.count)}>
                {staffCount}
              </span>
            </div>
            <div className={classes.summaryItem}>
              <span className="text-body">
                {t("common:student", { count: studentCount })}
              </span>
              <span className={classNames("text-body-medium", classes.count)}>
                {studentCount}
              </span>
            </div>
            <div className={classes.summaryItem}>
              <span className="text-body">
                {t("common:family_member", { count: familyCount })}
              </span>
              <span className={classNames("text-body-medium", classes.count)}>
                {familyCount}
              </span>
            </div>
          </div>
        </div>
      ) : null}

      <RightPreviewPanel
        t={t}
        sharedWith={sharedWith}
        participantListType={participantListType}
        grades={grades}
        courses={courses}
        userByUserType={userByUserType}
        isQuickViewModal={isQuickViewModal}
        isAssociatedEntitiesDetailsLoading={isAssociatedEntitiesDetailsLoading}
      />
    </div>
  );
};

ParticipantsPreview.propTypes = {
  participants: PropTypes.array,
  grades: PropTypes.array,
};

ParticipantsPreview.defaultProps = {
  participants: [],
  grades: [],
};

const mapStateToPropsLocal = (state, ownProps) => {
  const {
    isQuickViewModal,
    grades,
    courses,
    participantListTypeProp,
    sharedWithProp,
    userByUserTypeProp,
    userCountByEntityProp,
    isAssociatedEntitiesDetailsLoading,
  } = ownProps;
  const { eventModalV2 } = mapStateToProps(state);
  const organizationId = getOrganizationId(state);
  const academicYearId = getSelectedAcademicYearIdForSchool(state);

  const {
    entityType: participantListType,
    participants,
    sharedWith,
  } = eventModalV2;

  // Format participants for CUSTOM entity type
  const customParticipants = participants[ENTITY_TYPE.CUSTOM] || [];
  const formattedCustomParticipants = _.groupBy(
    customParticipants,
    "__typename"
  );
  const userByUserType = {
    [ENTITY_TYPE.STAFF]: formattedCustomParticipants["Staff"] || [],
    [ENTITY_TYPE.STUDENT]: formattedCustomParticipants["Student"] || [],
    [ENTITY_TYPE.PARENT]: formattedCustomParticipants["FamilyMember"] || [],
  };
  const userCountByEntity = {
    staffCount: _.size(_.get(userByUserType, ENTITY_TYPE.STAFF, [])),
    studentCount: _.size(_.get(userByUserType, ENTITY_TYPE.STUDENT, [])),
    familyCount: _.size(_.get(userByUserType, ENTITY_TYPE.PARENT, [])),
  };

  return {
    organizationId,
    academicYearId,

    // query variables
    queryFilter: undefined,
    isQuickViewModal,

    // component props
    participantListType: isQuickViewModal
      ? participantListTypeProp
      : MAP_LIST_TYPE_TO_QUERY_ENTITY[participantListType], // Takes GRADE & COURSE
    sharedWith: isQuickViewModal ? sharedWithProp : sharedWith,
    grades: isQuickViewModal ? grades : participants[ENTITY_TYPE.GRADES] || [],
    courses: isQuickViewModal
      ? courses
      : participants[ENTITY_TYPE.CLASSES] || courses,
    userByUserType: isQuickViewModal ? userByUserTypeProp : userByUserType,
    userCountByEntity: isQuickViewModal
      ? userCountByEntityProp
      : userCountByEntity,
    entityIds: _.map(participants[participantListType], "id"),

    // withloader props
    isData: true,
    isLoading: false,
    isAssociatedEntitiesDetailsLoading,
  };
};

export default compose(
  connect(mapStateToPropsLocal, mapActionCreators),
  graphql(getUserCountByEntityandEntityIds, {
    name: "userCountByEntityCourseWithEntityIds",
    skip: ({ participantListType, entityIds, isQuickViewModal }) =>
      !participantListType ||
      _.isEmpty(entityIds) ||
      participantListType === "CUSTOM" ||
      isQuickViewModal,
    options: ({
      organizationId,
      participantListType,
      entityIds,
      academicYearId,
    }) => ({
      fetchPolicy: "cache-and-network",
      context: { useHttpLink: true },
      variables: {
        organizationId,
        input: {
          entity: participantListType,
          entityIds,
          academicYearId,
        },
      },
    }),
    props: ({
      userCountByEntityCourseWithEntityIds,
      ownProps: { isData, isLoading },
    }) => {
      const queryData = _.get(userCountByEntityCourseWithEntityIds, "node", {});
      return {
        isData: !_.isEmpty(queryData) && isData,
        userCountByEntity: _.get(queryData, "users.userCountByEntity", {}),
        isLoading: _.includes(
          [1, 2],
          userCountByEntityCourseWithEntityIds.networkStatus || isLoading
        ),
      };
    },
  }),
  withLoader
)(ParticipantsPreview);
